import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Calendar,
  MapPin,
  Users,
  CheckCircle,
  Clock,
  ArrowRight,
  FileText
} from 'lucide-react';
import { useContent } from '../hooks/useContent';

const Events = () => {
  const navigate = useNavigate();
  const { content, loading, error } = useContent();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading content: {error}</p>
      </div>
    );
  }

  if (!content) return null;

  const {
    registrationOpenEvents,
    completedEvents,
    proposedEvents
  } = content;

  const handleRegister = () => {
    window.open('https://survey.zohopublic.in/zs/PyNtNi', '_blank');
  };

  return (
    <div className="events-page">
      <div className="events-hero">
        <div className="container">
          <h1 className="events-page-title">Our Retreats & Events</h1>
          <p className="events-page-subtitle">
            Transformative learning experiences for education leaders committed to inclusive practices
          </p>
        </div>
      </div>

      <div className="container events-content">
        <Tabs defaultValue="open" className="events-tabs">
          <TabsList className="events-tabs-list">
            <TabsTrigger value="open" className="events-tab-trigger">
              Registration Open
            </TabsTrigger>
            <TabsTrigger value="completed" className="events-tab-trigger">
              Completed
            </TabsTrigger>
            <TabsTrigger value="proposed" className="events-tab-trigger">
              Proposed
            </TabsTrigger>
          </TabsList>

          {/* Registration Open Tab */}
          <TabsContent value="open" className="events-tab-content">
            <div className="events-intro">
              <h2 className="events-section-title">Open for Registration</h2>
              <p className="events-section-description">
                Join us for these upcoming retreats. Limited spots available!
              </p>
            </div>
            <div className="events-grid">
              {registrationOpenEvents.map((event) => (
                <Card key={event.id} className="event-detail-card">
                  <CardHeader>
                    <div className="event-card-header">
                      <Badge className="event-badge-open">{event.status}</Badge>
                      <CardTitle className="event-card-title">{event.title}</CardTitle>
                    </div>
                    <CardDescription className="event-card-description">
                      {event.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="event-card-content">
                    <div className="event-details-list">
                      <div className="event-detail-row">
                        <Calendar className="event-detail-icon" />
                        <span>{event.date}</span>
                      </div>
                      <div className="event-detail-row">
                        <MapPin className="event-detail-icon" />
                        <span>{event.location}</span>
                      </div>
                      <div className="event-detail-row">
                        <Users className="event-detail-icon" />
                        <span>{event.format}</span>
                      </div>
                      <div className="event-detail-row">
                        <Users className="event-detail-icon" />
                        <span>Capacity: {event.capacity}</span>
                      </div>
                      <div className="event-detail-row">
                        <Clock className="event-detail-icon" />
                        <span>Deadline: {event.deadline}</span>
                      </div>
                    </div>

                    <div className="event-highlights">
                      <h4 className="highlights-title">Event Highlights</h4>
                      <ul className="highlights-list">
                        {event.highlights.map((highlight, index) => (
                          <li key={index}>
                            <CheckCircle className="highlight-icon" />
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button onClick={handleRegister} className="event-register-btn">
                      Register Now
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Completed Tab */}
          <TabsContent value="completed" className="events-tab-content">
            <div className="events-intro">
              <h2 className="events-section-title">Completed Retreats</h2>
              <p className="events-section-description">
                Celebrating the success of our past retreats and their impact on inclusive education
              </p>
            </div>
            <div className="events-grid">
              {completedEvents.map((event) => (
                <Card key={event.id} className="event-detail-card completed-card">
                  <div className="completed-event-images">
                    <img src={event.images[0]} alt={event.title} className="completed-main-image" />
                    <div className="completed-thumbnail-grid">
                      {event.images.slice(1).map((img, idx) => (
                        <img key={idx} src={img} alt={`${event.title} ${idx + 1}`} className="completed-thumbnail" />
                      ))}
                    </div>
                  </div>
                  <CardHeader>
                    <div className="event-card-header">
                      <Badge className="event-badge-completed">{event.status}</Badge>
                      <CardTitle className="event-card-title">{event.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="event-card-content">
                    <div className="event-details-list">
                      <div className="event-detail-row">
                        <Calendar className="event-detail-icon" />
                        <span>{event.date}</span>
                      </div>
                      <div className="event-detail-row">
                        <MapPin className="event-detail-icon" />
                        <span>{event.location}</span>
                      </div>
                      <div className="event-detail-row">
                        <Users className="event-detail-icon" />
                        <span>{event.format}</span>
                      </div>
                    </div>

                    <p className="event-description">{event.description}</p>

                    <div className="event-outcomes">
                      <h4 className="outcomes-title">Key Outcomes</h4>
                      <ul className="outcomes-list">
                        {event.outcomes.map((outcome, index) => (
                          <li key={index}>
                            <CheckCircle className="outcome-icon" />
                            <span>{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Proposed Tab */}
          <TabsContent value="proposed" className="events-tab-content">
            <div className="events-intro">
              <h2 className="events-section-title">Proposed Events</h2>
              <p className="events-section-description">
                Future initiatives we're planning to expand our impact. Stay tuned for updates!
              </p>
            </div>
            <div className="events-grid">
              {proposedEvents.map((event) => (
                <Card key={event.id} className="event-detail-card proposed-card">
                  <CardHeader>
                    <div className="event-card-header">
                      <Badge className="event-badge-proposed">{event.status}</Badge>
                      <CardTitle className="event-card-title">{event.title}</CardTitle>
                    </div>
                    <CardDescription className="event-card-description">
                      {event.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="event-card-content">
                    <div className="event-details-list">
                      <div className="event-detail-row">
                        <Calendar className="event-detail-icon" />
                        <span>{event.date}</span>
                      </div>
                      <div className="event-detail-row">
                        <MapPin className="event-detail-icon" />
                        <span>{event.location}</span>
                      </div>
                      <div className="event-detail-row">
                        <Users className="event-detail-icon" />
                        <span>{event.format}</span>
                      </div>
                      {event.expectedCapacity && (
                        <div className="event-detail-row">
                          <Users className="event-detail-icon" />
                          <span>Expected Capacity: {event.expectedCapacity}</span>
                        </div>
                      )}
                    </div>

                    <div className="event-focus">
                      <h4 className="focus-title">Focus Areas</h4>
                      <ul className="focus-list">
                        {event.focus.map((item, index) => (
                          <li key={index}>
                            <FileText className="focus-icon" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="proposed-note">
                      <p>Dates and details will be confirmed soon. Express your interest by contacting us.</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Events;
